import { ProfileFormSelect, ProfileFormTextField } from '@backrs_ui/common-ui';

import { ProfileValueOption } from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { Box, Typography, SxProps, Theme, Tooltip } from '@mui/material';
import { Controller, FieldValues, Control } from 'react-hook-form';

import { ProfileFieldConfig, Validation } from '../types/profile.types';
import {
  handleAcademicMajorChange,
  handleApTestChange,
  handleEntranceExamChange,
} from '../utils/fields.helpers';

type DynamicInputProps = Readonly<{
  selectedOption: ProfileValueOption & { score?: number; major_gpa?: number };
  field: ProfileFieldConfig;
  control: Control<FieldValues, object>;
  validateValue: (value: string, validation?: Validation) => boolean;
  getValues: () => FieldValues;
  disabled?: boolean;
}>;

export const DynamicInput = ({
  selectedOption,
  field,
  control,
  validateValue,
  getValues,
  disabled,
}: DynamicInputProps) => {
  if (!field.createDynamicInput || !field.dynamicInputConfig) return null;

  // Use dynamic config if available, otherwise fall back to static config
  const dynamicConfig = field.dynamicInputConfig.getDynamicConfig?.(selectedOption);

  const scoringOptions = dynamicConfig?.options || field.dynamicInputConfig.options || [];
  const placeholder = (dynamicConfig?.placeholder || field.dynamicInputConfig.placeholder) ?? '';
  const dynamicValidation = dynamicConfig?.validation || field.validation;
  const resolveDefaultValue = () => {
    if (field.key === 'major_gpa') {
      return selectedOption?.major_gpa ? String(selectedOption.major_gpa) : '';
    } else {
      return selectedOption?.score ? String(selectedOption.score) : '';
    }
  };
  const resolveLabel = () => {
    if (field.key === 'major_gpa') {
      return `GPA: ${selectedOption.name}`;
    } else {
      return selectedOption.name;
    }
  };
  const defaultValue = resolveDefaultValue();
  const label = resolveLabel();

  const textLeadingStartAdornment = (name: string) => {
    const content = (
      <Typography
        variant="text-md-regular"
        color={(theme) => theme.backrs.colors['Gray/600']}
        sx={textLeadingSx}
      >
        {name}
      </Typography>
    );

    return (
      <Box sx={startAdornmentContainerSx}>
        {name.length > 20 && (
          <Tooltip title={name} arrow placement="top">
            {content}
          </Tooltip>
        )}

        {name.length <= 20 && content}
      </Box>
    );
  };

  return (
    <Box
      key={`${field.key}_${selectedOption.id}`}
      sx={{
        width: {
          xs: '100%',
          md: field.dynamicInputConfig.width === 'half' ? 'calc(50% - 8px)' : '100%',
        },
      }}
    >
      <Controller
        name={`${field.key}_${selectedOption.id}`}
        control={control}
        defaultValue={defaultValue}
        render={({ field: controllerField, fieldState: { error } }) =>
          field.dynamicInputConfig?.type === 'SINGLE_SELECT' ? (
            <ProfileFormSelect
              key={field.key}
              isMultiple={false}
              showHoverDelete={false}
              options={scoringOptions}
              chipType={field.chipType}
              textLeadingAdornment={{ name: label }}
              field={{
                name: field.key,
                placeholder: placeholder,
                selectOptions: {
                  type: 'SINGLE',
                  options: scoringOptions,
                },
              }}
              controllerField={{
                ...controllerField,
                onChange: (e) => {
                  controllerField.onChange(e);
                  const values = Array.isArray(e) ? e : [e];

                  if (field.key === 'academicApTests' || field.key === 'major_gpa') {
                    // Get all currently selected AP test IDs from defaultValue
                    const allSelectedIds = Array.isArray(field.defaultValue)
                      ? field.defaultValue.map((option) =>
                          typeof option === 'string' ? option : option.id,
                        )
                      : [];

                    if (field.key === 'academicApTests') {
                      handleApTestChange(field, allSelectedIds, getValues);
                    }

                    return;
                  }

                  if (validateValue(values[0], dynamicValidation)) {
                    field.onChange?.(values[0] ? [Number(values[0])] : []);
                  }
                },
              }}
              groupedFields={{ [field.key]: [field as { name?: string }] }}
              index={0}
              error={error}
              groupKey={field.key}
            />
          ) : (
            <ProfileFormTextField
              field={{
                name: `${field.key}_${selectedOption.id}`,
                placeholder: placeholder,
                customStartAdornment: textLeadingStartAdornment(label),
                label: label,
              }}
              useTimeout={false}
              groupedFields={{ [field.key]: [field as { name?: string }] }}
              index={0}
              controllerField={{
                ...controllerField,
                value: defaultValue,
                onChange: (e) => {
                  controllerField.onChange(e);
                  const value = e.target.value;

                  if (field.key === 'academicEntranceExams' || field.key === 'major_gpa') {
                    if (validateValue(value, dynamicValidation)) {
                      // Get all currently selected IDs from defaultValue
                      const allSelectedIds = Array.isArray(field.defaultValue)
                        ? field.defaultValue.map((option) =>
                            typeof option === 'string' ? option : option.id,
                          )
                        : [];

                      if (field.key === 'major_gpa') {
                        handleAcademicMajorChange(field, allSelectedIds, getValues);
                      } else {
                        handleEntranceExamChange(field, allSelectedIds, getValues);
                      }
                    }

                    return;
                  }
                  if (validateValue(value, dynamicValidation)) {
                    field.onChange?.(value ? [Number(value)] : []);
                  }
                },
              }}
              error={error?.message}
              disabled={disabled}
              validation={dynamicValidation}
            />
          )
        }
      />
    </Box>
  );
};

const startAdornmentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  alignItems: 'center',
  paddingRight: backrs.spacing['spacing-md'],
  maxWidth: { lg: '175px' },
  width: { lg: '175px' },
  display: {
    xs: 'none',
    sm: 'flex',
  },
});

const textLeadingSx: SxProps<Theme> = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  width: '100%',
};
